// VIVA-1214 — parámetros que Stripe agrega a la URL al volver de una
// autenticación 3DS.
//
// `confirmPayment` va con `redirect: "if_required"`: con la mayoría de las
// tarjetas el pago se confirma sin salir de la página, pero si el banco exige
// el reto 3DS el navegador se va al banco y vuelve al `return_url` con
// `payment_intent`, `payment_intent_client_secret` y `redirect_status`
// agregados. Hasta el 25/09/2026 nadie los leía: el comprador regresaba y la
// pantalla se veía como si no hubiera pasado nada.
//
// POR QUÉ SE BUSCAN EN DOS SITIOS: esta aplicación usa `HashRouter`, así que el
// `return_url` es `https://.../#/guides2/contratar`. Stripe puede agregar los
// parámetros antes del fragmento (`...?payment_intent=pi_1#/guides2/contratar`,
// que es lo correcto según la norma de URL) o dentro de él
// (`...#/guides2/contratar?payment_intent=pi_1`). No se puede depender de cuál
// de las dos haga, y equivocarse significa perder el pago de alguien, así que
// se leen las dos y se limpian las dos.
export const STRIPE_RETURN_PARAMS = [
  "payment_intent",
  "payment_intent_client_secret",
  "redirect_status",
];

// El fragmento de HashRouter es `#/ruta?a=1`: lo que va después del primer `?`
// es su query.
const hashQuery = (hash = "") => {
  const corte = hash.indexOf("?");
  return corte >= 0 ? hash.slice(corte + 1) : "";
};

const parseUrl = (href) => {
  try {
    return new URL(href);
  } catch {
    return null;
  }
};

// Devuelve el retorno de Stripe, o null si esta carga de la página no viene de
// una autenticación 3DS.
export const readStripeReturn = (href = window.location.href) => {
  const url = parseUrl(href);
  if (!url) return null;

  const enHash = new URLSearchParams(hashQuery(url.hash));
  const leer = (name) => url.searchParams.get(name) || enHash.get(name) || "";

  const paymentIntentId = leer("payment_intent");
  if (!paymentIntentId) return null;

  return {
    paymentIntentId,
    clientSecret: leer("payment_intent_client_secret"),
    // Lo que Stripe dice que pasó. NO habilita nada: la URL la puede escribir
    // cualquiera. Solo sirve para no ponerse a preguntar por un pago que el
    // propio Stripe ya reportó como fallido.
    redirectStatus: leer("redirect_status"),
  };
};

// La misma URL sin los parámetros de Stripe, conservando el resto (incluida la
// ruta del HashRouter y cualquier otro parámetro que ya viniera).
export const urlWithoutStripeReturn = (href = window.location.href) => {
  const url = parseUrl(href);
  if (!url) return href;

  STRIPE_RETURN_PARAMS.forEach((name) => url.searchParams.delete(name));

  const query = hashQuery(url.hash);
  if (query) {
    const restantes = new URLSearchParams(query);
    STRIPE_RETURN_PARAMS.forEach((name) => restantes.delete(name));
    const cola = restantes.toString();
    url.hash = url.hash.slice(0, url.hash.indexOf("?")) + (cola ? `?${cola}` : "");
  }

  return url.toString();
};

// Se limpia ANTES de resolver el pago: si la persona recarga mientras se
// consulta, la consulta no vuelve a empezar, y la liga que pudiera copiar o
// dejar en el historial no lleva el identificador de su pago.
export const clearStripeReturnFromUrl = () => {
  try {
    window.history.replaceState(null, "", urlWithoutStripeReturn());
  } catch (error) {
    console.error("Error al limpiar los parámetros de Stripe de la URL:", error);
  }
};
